import { CloseOutlineIcon } from '@applyboard/ui-icons';
import { css } from '@emotion/react';
import React, { useRef, useState, useEffect } from 'react';
import { Tooltip } from '../Tooltip';
import { useFullTheme } from '~/design';
import { IconType, MultivariantIconType } from '~/design/types';
import { IconWrapper, TooltipCompatibleProps } from '~/internal';
import { useResponsive } from '~/utils';
import { allowedTooltipSymbols } from '~/utils/tooltip';
import { SrOnly } from '../SrOnly';

const CHIP_MAX_WIDTH = 220;

export interface ChipProps extends TooltipCompatibleProps {
  /** The text of the chip. */
  children?: string;

  /** Additional text that is rendered for screen reader users when the chip can be deleted. */
  deleteInstructions?: string;

  /** If true, the chip cannot be interacted with. */
  disabled?: boolean;

  /** The emphasis of the chip against its background. (default: highlighted) */
  emphasis?: 'highlighted' | 'outlined' | 'plain';

  /** The icon that appears on the left side of the chip. */
  icon?: IconType | MultivariantIconType;

  /** A callback function that is invoked when the chip is clicked. */
  onClick?: () => void;

  /** A callback function that is invoked when the "X" is clicked. Passing this function will cause the "X" to appear. */
  onDelete?: () => void;

  /** If true, the chip appears as selected. */
  selected?: boolean;

  /** The size of the chip. */
  size?: 'xs' | 'sm' | 'md';

  /**
   * The display variation of the chip.
   * @deprecated Use `emphasis` instead.
   */
  variant?: 'highlighted' | 'outlined' | 'plain';

  /** The (max) width of the chip. `md` will set a max width of 220px, `hug` will make the chip fit its
   * content. (default: md) */
  width?: 'md' | 'hug';

  /**
   * Use this prop if you're showing/hiding content based off clicking this chip
   *
   * @see: https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/
   */
  'aria-expanded'?: boolean;
}

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(function Chip(props, ref) {
  if (!props.children && !!props.onDelete) {
    throw new Error('`onDelete` prop cannot be supplied for icon-only Chips.');
  }

  const chipRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setShowTooltip(props.width === 'md' && chipRef.current?.offsetWidth === CHIP_MAX_WIDTH);
  }, [props.width]);

  if (showTooltip && props.children) {
    return (
      <Tooltip label={props.children}>
        <InnerChip {...props} ref={ref} chipRef={chipRef} maxTextWidth={CHIP_MAX_WIDTH} />
      </Tooltip>
    );
  }

  return <InnerChip {...props} ref={ref} chipRef={chipRef} maxTextWidth={CHIP_MAX_WIDTH} />;
});

Object.assign(Chip, { [allowedTooltipSymbols.Chip]: true });
interface InnerChipProps extends ChipProps, TooltipCompatibleProps {
  chipRef: React.RefObject<HTMLDivElement>;
  maxTextWidth: number;
}

const InnerChip = React.forwardRef<HTMLButtonElement, InnerChipProps>(function InnerChip(
  {
    children,
    chipRef,
    deleteInstructions,
    disabled = false,
    emphasis: _emphasis = 'highlighted',
    icon: Icon,
    maxTextWidth,
    onBlur,
    onClick,
    onDelete,
    onFocus,
    onPointerDown,
    onPointerLeave,
    onPointerMove,
    selected = false,
    size = 'md',
    variant,
    width = 'md',
    'aria-expanded': ariaExpanded,
  },
  ref,
) {
  const emphasis = variant ?? _emphasis;
  const theme = useFullTheme();
  const responsive = useResponsive();
  const { borders, borderRadiuses, colors, spacing, typography } = theme;

  const deleteIconWidth =
    size === 'md'
      ? parseInt(spacing[3]) * 2 + 24
      : parseInt(spacing[2]) + parseInt(spacing[3]) + 24;

  const height = heightMap[size];
  const sizeStyles = {
    xs: css({
      height: spacing[6],
      ...responsive.css(typography.labelS),
    }),
    sm: css({
      height: spacing[8],
      ...responsive.css(typography.labelM),
    }),
    md: css({
      height: spacing[12],
      ...responsive.css(typography.labelL),
    }),
  };

  const emphasisStyles = {
    highlighted: css([
      {
        backgroundColor: colors.bgSecondaryHighlight,
        ...generatePseudoStyle('hover', colors.bgSecondaryHighlightHover, !!onClick),
        ...generatePseudoStyle('active', colors.bgSecondaryHighlightPressed, !!onClick),
      },
      borders.transparent,
    ]),
    outlined: css([
      {
        backgroundColor: colors.bgRegular,
        ...generatePseudoStyle('hover', colors.bgRegularHover, !!onClick),
        ...generatePseudoStyle('active', colors.bgRegularPressed, !!onClick),
      },
      borders.secondary,
    ]),
    plain: css([
      {
        backgroundColor: colors.bgRegular,
        ...generatePseudoStyle('hover', colors.bgRegularHover, !!onClick),
        ...generatePseudoStyle('active', colors.bgRegularPressed, !!onClick),
      },
      borders.transparent,
    ]),
  };

  const selectedStyle = css([
    {
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimaryStark,
      ...generatePseudoStyle('hover', colors.bgPrimaryHover, !!onClick),
      ...generatePseudoStyle('active', colors.bgPrimaryPressed, !!onClick),
    },
    borders.transparent,
  ]);

  const disabledStyle = css({
    color: colors.textInactive,
    cursor: 'not-allowed',
    ...generatePseudoStyle('hover', colors.bgInactive, !!onClick),
    ...generatePseudoStyle('active', colors.bgInactive, !!onClick),
  });

  const disabledEmphasisStyles = {
    highlighted: css({
      backgroundColor: colors.bgInactive,
    }),
    outlined: css({
      backgroundColor: colors.bgInactive,
      borderColor: colors.borderInactive,
    }),
    plain: css({
      backgroundColor: colors.bgRegular,
      ...generatePseudoStyle('hover', colors.bgRegular, !!onClick),
      ...generatePseudoStyle('active', colors.bgRegular, !!onClick),
    }),
  };

  const selectedDisabledStyle = css({
    backgroundColor: colors.bgPrimaryHighlight,
    borderColor: 'transparent',
    color: colors.borderPrimary, // TODO: use a better token
    cursor: 'not-allowed',
    ...generatePseudoStyle('hover', colors.bgPrimaryHighlight, !!onClick),
    ...generatePseudoStyle('active', colors.bgPrimaryHighlight, !!onClick),
  });

  const deleteIconStyles = css({
    cursor: 'pointer',
    alignItems: 'center',
    borderTopRightRadius: borderRadiuses.control,
    borderBottomRightRadius: borderRadiuses.control,
    borderLeft: 'none',
    display: 'flex',
    paddingLeft: spacing[3],
    paddingRight: size === 'md' ? spacing[3] : spacing[2],
    position: 'absolute',
    right: 0,
    width: `${deleteIconWidth}px`,
    '&:hover': {
      backgroundColor: selected ? colors.bgPrimaryHover : colors.bgSecondaryHover,
    },
    '&:active': {
      backgroundColor: selected ? colors.bgPrimaryPressed : colors.bgSecondaryPressed,
    },
  });

  const commonStyles = [
    emphasisStyles[emphasis],
    selected && selectedStyle,
    disabled && disabledStyle,
    disabled && disabledEmphasisStyles[emphasis],
    selected && disabled && selectedDisabledStyle,
  ];

  const chipTextStyle = css({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  });
  return (
    <div
      css={{
        display: 'inline-flex',
        position: 'relative',
        maxWidth: width === 'md' ? CHIP_MAX_WIDTH + deleteIconWidth : undefined,
      }}
    >
      <button
        css={[
          sizeStyles[size],
          {
            backgroundColor: 'transparent',
            border: 0,
            borderRadius: borderRadiuses.control,
            cursor: onClick ? 'pointer' : undefined,
            height,
            margin: 0,
            padding: 0,
            paddingRight: `${onDelete ? deleteIconWidth : 0}px`,
            '&:focus-visible': {
              outline: `2px solid ${theme.colors.actionPrimary}`,
            },
          },
        ]}
        disabled={disabled}
        onBlur={() => onBlur?.()}
        onClick={() => onClick?.()}
        onKeyDown={event => {
          if (disabled) {
            return;
          }
          if ([8, 46].includes(event.keyCode)) {
            // Backspace, Delete keys
            onDelete?.();
          }
        }}
        onFocus={onFocus}
        onPointerDown={onPointerDown}
        onPointerLeave={onPointerLeave}
        onPointerMove={onPointerMove}
        ref={ref}
        type="button"
        aria-expanded={ariaExpanded}
      >
        <div
          css={[
            {
              alignItems: 'center',
              borderRadius: borderRadiuses.control,
              color: colors.textSecondaryStark,
              display: 'flex',
              gap: Icon ? spacing[2] : 0,
              height: '100%',
              maxWidth: width === 'md' ? `${maxTextWidth}px` : undefined,
              minWidth: '0px',
              paddingLeft: Icon ? spacing[3] : spacing[4],
              paddingRight: onDelete ? spacing[2] : spacing[4],
            },
            ...commonStyles,
            !!onDelete && {
              borderRight: 'none',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          ]}
          ref={chipRef}
        >
          {Icon && (
            <IconWrapper
              disabled={disabled}
              intent={selected ? 'primary' : 'secondary'}
              selector="button"
            >
              <Icon variant={selected ? 'fill' : 'outline'} />
            </IconWrapper>
          )}
          <span css={chipTextStyle}>{children}</span>
          {!!onDelete && (
            <SrOnly>
              {deleteInstructions ? `(${deleteInstructions})` : '(Press delete key to remove)'}
            </SrOnly>
          )}
        </div>
      </button>
      {onDelete && (
        <>
          <button
            aria-hidden
            aria-label="Delete chip"
            // when navigating through with a keyboard, pressing delete or backspace
            // on the chip element will be enough to delete it.
            tabIndex={-1}
            css={[sizeStyles[size], deleteIconStyles, ...commonStyles, { borderLeft: 'none' }]}
            type="button"
            onClick={e => {
              if (disabled) {
                return;
              }
              e.stopPropagation();
              onDelete();
            }}
          >
            <IconWrapper
              contrast="high"
              disabled={disabled}
              intent={selected ? 'primary' : 'secondary'}
              selector="button"
            >
              <CloseOutlineIcon />
            </IconWrapper>
          </button>
        </>
      )}
    </div>
  );
});

Object.assign(InnerChip, { [allowedTooltipSymbols.InnerChip]: true });

function generatePseudoStyle(pseudoClass: 'active' | 'hover', color: string, hasOnClick: boolean) {
  if (!hasOnClick && pseudoClass === 'hover') return;

  return {
    [`&:${pseudoClass}`]: {
      backgroundColor: color,
      '&+ div': {
        backgroundColor: color,
      },
    },
  };
}

export const heightMap = {
  xs: 24,
  sm: 32,
  md: 48,
};
